import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { 
  ArrowRight, 
  BookOpen, 
  CheckCircle2, 
  Sparkles, 
  Shield, 
  ChevronRight,
  ChevronLeft,
  RotateCcw,
  ExternalLink,
  Quote,
  Star,
  Globe2,
  Building2,
  BookMarked,
  Languages
} from 'lucide-react';

interface EndorsementItem {
  id: string;
  endorserName: string;
  endorserTitle: string;
  quote: string;
  bookTitle: string;
  bookAuthor: string;
  bookSubtitle: string;
  formatBadge: string;
  coverImage: string;
  productUrl: string;
  catalogUrl: string;
  tag: string;
}

const CURATED_ENDORSEMENTS: EndorsementItem[] = [
  {
    id: 'end-01',
    endorserName: 'Tim Challies',
    endorserTitle: 'Blogger; author, Seasons of Sorrow',
    quote: 'If you are looking for wise and perceptive guidance to help you see and experience the deep impact of God’s words in Scripture, you will love this devotional. Each day’s reflection is a gem, focusing the light of the chosen Scripture text onto our thinking, our affections and our behaviour. I cannot imagine it being read without benefit!',
    bookTitle: 'Truth for Life: Volume 2',
    bookAuthor: 'Alistair Begg',
    bookSubtitle: '365 gospel-centered reflections anchored in God\'s Word, crafted in Smyth-sewn archival binding.',
    formatBadge: 'Hardcover Devotional',
    coverImage: 'https://forthetruth.in/wp-content/uploads/2022/10/truth-for-life-front-1.jpg',
    productUrl: 'https://forthetruth.in/product/truth-for-life-volume-2/',
    catalogUrl: '/publications/truth-for-life-volume-2',
    tag: 'Daily Devotional • Vol. 2',
  },
  {
    id: 'end-02',
    endorserName: 'Rosaria Butterfield',
    endorserTitle: 'Author, The Secret Thoughts of an Unlikely Convert',
    quote: 'Sin is subtle, deceitful, and relentless. Garrett Kell doesn’t give trite answers or superficial formulas; he points us directly to the power of Christ, the honesty of confession, and the refuge of divine grace. A profoundly practical and biblical guide for every believer.',
    bookTitle: 'How Do I Fight Sin and Temptation?',
    bookAuthor: 'Garrett Kell',
    bookSubtitle: 'Confronting the spiritual weight of sin with practical, biblical pathways to enduring freedom in Christ.',
    formatBadge: 'Paperback Edition',
    coverImage: 'https://forthetruth.in/wp-content/uploads/2025/05/e79ff3000993512b7ad92a2090ef0f4b183e2c91-e1750140482205.webp',
    productUrl: 'https://forthetruth.in/product/how-do-i-fight-sin-and-temptation/',
    catalogUrl: '/publications/how-do-i-fight-sin-and-temptation',
    tag: 'Church Questions Series',
  },
  {
    id: 'end-03',
    endorserName: 'Kevin DeYoung',
    endorserTitle: 'Senior Pastor, Christ Covenant Church; Author, Just Do Something',
    quote: 'Dane Ortlund handles one of Scripture’s most sobering and contested doctrines with pastoral gentleness, unflinching biblical fidelity, and deep gospel clarity. This is essential, urgent reading for anyone seeking the truth about eternity.',
    bookTitle: 'Is Hell Real?',
    bookAuthor: 'Dane C. Ortlund',
    bookSubtitle: 'A pastorally sensitive, biblically unyielding examination of eternal realities and the hope of the cross.',
    formatBadge: 'Paperback Edition',
    coverImage: 'https://forthetruth.in/wp-content/uploads/2025/05/5b276cc3f8ba407fb1e0fde33a7b5b9bcad2dc8c-e1750140721597.webp',
    productUrl: 'https://forthetruth.in/product/is-hell-real/',
    catalogUrl: '/publications/is-hell-real',
    tag: 'Doctrinal Foundations',
  },
  {
    id: 'end-04',
    endorserName: 'Mark Dever',
    endorserTitle: 'Senior Pastor, Capitol Hill Baptist Church; President, 9Marks',
    quote: 'Generosity in the local church is not an administrative burden—it is a spiritual privilege and a vital act of worship. Jamie Dunlop provides a biblical, compelling, and intensely practical framework for sacrificial kingdom giving.',
    bookTitle: 'Why Should I Give to My Church?',
    bookAuthor: 'Jamie Dunlop',
    bookSubtitle: 'Understanding biblical stewardship, church membership, and joyful financial partnership in the gospel.',
    formatBadge: 'Paperback Edition',
    coverImage: 'https://forthetruth.in/wp-content/uploads/2025/05/bcaab0ae497157568709b3259db02a7f1bc93c82-e1750140862755.webp',
    productUrl: 'https://forthetruth.in/product/why-should-i-give-to-my-church/',
    catalogUrl: '/publications/why-should-i-give-to-my-church',
    tag: 'Stewardship & Church Life',
  },
  {
    id: 'end-05',
    endorserName: 'Nancy DeMoss Wolgemuth',
    endorserTitle: 'Author and Teacher/Host, Revive Our Hearts',
    quote: 'Hospitality is the heartbeat of Christian fellowship and practical evangelism. Pat Ennis reminds us that opening our homes and hearts to others is not about worldly entertaining, but about ministering the welcoming love of Jesus.',
    bookTitle: 'How Can I Grow in Hospitality?',
    bookAuthor: 'Pat Ennis',
    bookSubtitle: 'Biblical wisdom for cultivating welcoming homes, sharing the gospel, and serving the body of Christ.',
    formatBadge: 'Paperback Edition',
    coverImage: 'https://forthetruth.in/wp-content/uploads/2025/05/ca904bb26edffa9a7dcc29284dd15069b2ff44e6-e1750139808616.webp',
    productUrl: 'https://forthetruth.in/product/how-can-i-grow-in-hospitality/',
    catalogUrl: '/publications/how-can-i-grow-in-hospitality',
    tag: 'Christian Living & Ministry',
  },
];

export const HomePage: React.FC = () => {
  const { data: publicationsRes } = useQuery({
    queryKey: ['public-publications', { featured: 'true' }],
    queryFn: () => api.get('/public/publications?featured=true').then((res) => res.data.data),
  });

  const { data: testimonialsRes } = useQuery({
    queryKey: ['public-testimonials'],
    queryFn: () => api.get('/public/testimonials').then((res) => res.data.data),
  });

  const publications = publicationsRes || [];
  const testimonials = testimonialsRes || [];

  // Dynamic endorsement selection on every page load / refresh
  const [activeEndorsementIndex, setActiveEndorsementIndex] = useState(() => 
    Math.floor(Math.random() * CURATED_ENDORSEMENTS.length)
  );

  const activeEndorsement = CURATED_ENDORSEMENTS[activeEndorsementIndex];

  const handleNextEndorsement = () => {
    setActiveEndorsementIndex((prev) => (prev + 1) % CURATED_ENDORSEMENTS.length);
  };

  const handlePrevEndorsement = () => {
    setActiveEndorsementIndex((prev) => (prev - 1 + CURATED_ENDORSEMENTS.length) % CURATED_ENDORSEMENTS.length);
  };

  const handleShuffle = () => {
    setActiveEndorsementIndex((prev) => {
      let next = Math.floor(Math.random() * CURATED_ENDORSEMENTS.length);
      if (next === prev) next = (prev + 1) % CURATED_ENDORSEMENTS.length;
      return next;
    });
  };

  return (
    <div className="space-y-16 pb-20">
      
      {/* 1. CORPORATE PUBLISHING HERO - BEAUTIFIED, ELEGANT & UNCLUTTERED */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#060911] via-[#0B1324] to-[#070A12] text-white pt-14 sm:pt-20 pb-16 sm:pb-22 border-b border-slate-800/80 font-sans">
        {/* Soft atmospheric ambient lighting */}
        <div className="absolute inset-0 opacity-[0.06] bg-[radial-gradient(#C29F78_1px,transparent_1px)] [background-size:32px_32px]"></div>
        <div className="absolute top-10 left-1/2 -translate-x-1/2 w-[700px] h-[380px] bg-amber-500/10 rounded-full blur-[150px] pointer-events-none"></div>
        <div className="absolute -top-24 right-10 w-[450px] h-[450px] bg-brand-500/10 rounded-full blur-[130px] pointer-events-none"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10 items-center">
            
            {/* Left Column: Authoritative Editorial Storytelling */}
            <div className="lg:col-span-7 space-y-6 text-center lg:text-left">
              
              {/* Official Corporate Badge */}
              <div className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300 text-[11px] font-mono tracking-wider uppercase font-semibold backdrop-blur-md">
                <Building2 className="w-3.5 h-3.5 text-amber-400" />
                <span>FOR THE TRUTH PRESS PRIVATE LIMITED</span>
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                <span className="text-[10px] text-slate-300 font-normal hidden sm:inline">INCORPORATED PRESS</span>
              </div>

              {/* Master Headline */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-black tracking-tight leading-[1.12] text-white">
                Publishing Literature of{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F6D285] via-[#E2A748] to-[#C98A2C]">
                  Eternal Weight
                </span>{' '}
                &amp; Enduring Truth.
              </h1>

              {/* Sub-headline: Rich Publishing Context */}
              <p className="text-sm sm:text-base text-slate-300 max-w-xl mx-auto lg:mx-0 leading-relaxed font-normal">
                India’s premier theological press and rights custodian, dedicated to publishing classical Christian scholarship, puritan treatises, and faithful doctrinal editions. Crafted with archival-grade typography, smyth-sewn binding, and statutory Government of India ISBN allotment.
              </p>

              {/* Clean, Refined Feature Badges (No stats, no cluttered language counts) */}
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-2.5 pt-1 text-left">
                <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-900/80 border border-slate-800 text-xs text-slate-300 backdrop-blur-md shadow-xs">
                  <CheckCircle2 className="w-4 h-4 text-amber-400 shrink-0" />
                  <span className="font-medium">Govt. of India ISBN (<span className="text-amber-300 font-mono">isbn.gov.in</span>)</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-900/80 border border-slate-800 text-xs text-slate-300 backdrop-blur-md shadow-xs">
                  <BookMarked className="w-4 h-4 text-amber-400 shrink-0" />
                  <span className="font-medium">Archival Smyth-Sewn Production</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-900/80 border border-slate-800 text-xs text-slate-300 backdrop-blur-md shadow-xs">
                  <Globe2 className="w-4 h-4 text-brand-400 shrink-0" />
                  <span className="font-medium">Pan-India Seminary &amp; Church Reach</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3.5 pt-2">
                <Link
                  to="/publications"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-gradient-to-r from-amber-500 to-brand-500 hover:from-amber-400 hover:to-brand-400 text-slate-950 font-bold text-xs uppercase tracking-wider shadow-lg shadow-amber-500/20 transition-all hover:scale-[1.01]"
                >
                  <BookOpen className="w-4 h-4" />
                  <span>Explore Production Archive (432 Titles)</span>
                  <ArrowRight className="w-4 h-4 ml-0.5" />
                </Link>
                
                <Link
                  to="/contact"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-slate-900/90 hover:bg-slate-800 text-slate-200 hover:text-white font-semibold text-xs border border-slate-700 hover:border-slate-600 transition-all"
                >
                  <Building2 className="w-4 h-4 text-amber-400" />
                  <span>Rights &amp; Translation Enquiries</span>
                </Link>
              </div>

              {/* Trust Anchor & Direct Bookstore Portal */}
              <div className="pt-1 flex items-center justify-center lg:justify-start gap-4 text-xs text-slate-400">
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                  <span>Official Imprint: For The Truth Publishing</span>
                </span>
                <span className="text-slate-700">&bull;</span>
                <a 
                  href="https://forthetruth.in/shop" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="hover:text-amber-300 transition-colors inline-flex items-center gap-1.5 font-semibold text-slate-300"
                >
                  <span>Bookstore Retail Portal</span>
                  <ExternalLink className="w-3 h-3 text-amber-400" />
                </a>
              </div>

            </div>

            {/* Right Column: High-End Layered 3D Book Showcase (Dynamically Generated) */}
            <div className="lg:col-span-5 relative">
              <div className="relative mx-auto max-w-sm sm:max-w-md lg:max-w-none">
                
                {/* Visual Glow behind Book */}
                <div className="absolute -inset-1 bg-gradient-to-r from-amber-500/20 via-brand-500/20 to-amber-700/20 rounded-3xl blur-xl opacity-75"></div>

                <div className="relative p-6 sm:p-7 rounded-3xl bg-gradient-to-b from-slate-900/95 via-slate-950/95 to-slate-900/95 border border-slate-800/90 shadow-2xl space-y-5 backdrop-blur-xl transition-all">
                  
                  {/* Status Bar */}
                  <div className="flex items-center justify-between border-b border-slate-800/80 pb-3.5">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></div>
                      <span className="text-[11px] font-mono uppercase tracking-wider text-slate-300 font-bold">
                        Featured Production
                      </span>
                    </div>
                    <span className="text-[10px] font-mono text-amber-300 font-bold bg-amber-950/60 px-2.5 py-1 rounded-full border border-amber-700/40">
                      Verified ISBN Allotment
                    </span>
                  </div>

                  {/* Physical Book Showcase */}
                  <div className="flex gap-5 items-center">
                    {/* Book Mockup with Hardcover Depth */}
                    <div className="relative shrink-0 group">
                      <div className="w-28 sm:w-32 aspect-[3/4] rounded-lg overflow-hidden shadow-2xl shadow-black/80 border border-amber-900/30 bg-slate-900 p-1.5 relative transition-transform duration-300 group-hover:scale-105">
                        {/* Book Spine Simulation Accent */}
                        <div className="absolute left-0 top-0 bottom-0 w-2.5 bg-gradient-to-r from-black/40 via-white/10 to-transparent z-10 pointer-events-none"></div>
                        <img
                          src={activeEndorsement.coverImage}
                          alt={activeEndorsement.bookTitle}
                          className="w-full h-full object-contain drop-shadow-md rounded"
                        />
                      </div>
                      <div className="absolute -bottom-2 -right-2 bg-brand-950 text-amber-300 text-[9px] font-mono font-bold px-2 py-0.5 rounded border border-amber-500/40 shadow-sm">
                        {activeEndorsement.formatBadge.split(' ')[0]}
                      </div>
                    </div>

                    {/* Book Metadata & Editorial Context */}
                    <div className="space-y-1.5 overflow-hidden">
                      <div className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase font-bold text-amber-400 tracking-wider">
                        <Sparkles className="w-3 h-3 text-amber-400" />
                        <span>{activeEndorsement.tag}</span>
                      </div>
                      <h3 className="font-serif font-bold text-lg text-white tracking-tight leading-snug">
                        {activeEndorsement.bookTitle}
                      </h3>
                      <p className="text-xs text-slate-300 font-medium">
                        By {activeEndorsement.bookAuthor}
                      </p>
                      <p className="text-[11px] text-slate-400 leading-relaxed line-clamp-2">
                        {activeEndorsement.bookSubtitle}
                      </p>
                      
                      <div className="pt-1 flex items-center gap-1.5 text-[10px] text-emerald-400 font-mono font-medium">
                        <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                        <span>Available in Bookstore</span>
                      </div>
                    </div>
                  </div>

                  {/* Endorsement Preview Quote */}
                  <div className="p-3 rounded-xl bg-slate-950/80 border border-slate-800/80 text-[11px] text-slate-300 italic font-serif leading-relaxed">
                    &ldquo;{activeEndorsement.quote}&rdquo;
                    <span className="block not-italic font-sans font-bold text-[10px] text-amber-400 mt-1">
                      — {activeEndorsement.endorserName}, {activeEndorsement.endorserTitle}
                    </span>
                  </div>

                  {/* Direct Link Action Footer */}
                  <div className="pt-1 flex items-center justify-between gap-3 text-xs border-t border-slate-800/60">
                    <Link
                      to={activeEndorsement.catalogUrl}
                      className="text-slate-300 hover:text-white font-medium inline-flex items-center gap-1"
                    >
                      <span>Catalog Details</span>
                      <ChevronRight className="w-3.5 h-3.5 text-amber-400" />
                    </Link>
                    <a
                      href={activeEndorsement.productUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3.5 py-1.5 rounded-lg bg-amber-500/20 hover:bg-amber-500 text-amber-300 hover:text-slate-950 font-bold border border-amber-500/40 transition-all inline-flex items-center gap-1.5"
                    >
                      <span>Store Page</span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>

                </div>

              </div>
            </div>

          </div>
        </div>

      </section>

      {/* 2. DYNAMIC SPOTLIGHT ENDORSEMENT & CRITICAL ACCLAIM (GENERATED DYNAMICALLY ON REFRESH) */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 text-white border border-slate-800 shadow-xl p-8 sm:p-12">
          
          <div className="absolute top-0 right-0 -mr-16 -mt-16 w-80 h-80 bg-brand-500/10 rounded-full blur-3xl pointer-events-none"></div>

          <div className="relative space-y-6">
            
            {/* Header with Title and Dynamic Navigation Controls */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-800/80 pb-4">
              <div className="flex items-center gap-2">
                <span className="px-3 py-1 rounded-full bg-brand-950 border border-brand-500/40 text-brand-300 text-[10px] font-mono uppercase tracking-wider font-bold">
                  Critical Acclaim &amp; Endorsements
                </span>
                <span className="text-[10px] font-mono text-slate-400 hidden sm:inline">
                  Generated Dynamically on Every Refresh
                </span>
              </div>

              {/* Interactive Carousel & Shuffle Controls */}
              <div className="flex items-center gap-2 self-start sm:self-auto">
                <button
                  onClick={handlePrevEndorsement}
                  aria-label="Previous Endorsement"
                  className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition-colors cursor-pointer border border-slate-700"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>

                {/* Dots indicator */}
                <div className="flex items-center gap-1 px-1">
                  {CURATED_ENDORSEMENTS.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setActiveEndorsementIndex(idx)}
                      className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
                        idx === activeEndorsementIndex ? 'w-4 bg-amber-400' : 'bg-slate-700 hover:bg-slate-500'
                      }`}
                      aria-label={`Go to endorsement ${idx + 1}`}
                    />
                  ))}
                </div>

                <button
                  onClick={handleNextEndorsement}
                  aria-label="Next Endorsement"
                  className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition-colors cursor-pointer border border-slate-700"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>

                <button
                  onClick={handleShuffle}
                  className="ml-1 inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 border border-amber-500/30 text-[11px] font-mono font-medium transition-all cursor-pointer"
                  title="Shuffle to another review"
                >
                  <RotateCcw className="w-3 h-3" />
                  <span className="hidden sm:inline">Shuffle</span>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              
              {/* Left: The Quote */}
              <div className="lg:col-span-8 space-y-5">
                <Quote className="w-10 h-10 text-brand-400/40" />

                <blockquote className="text-lg sm:text-2xl font-serif italic text-slate-100 leading-relaxed transition-all">
                  &ldquo;{activeEndorsement.quote}&rdquo;
                </blockquote>

                <div className="pt-2 flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-t border-slate-800/80">
                  <div>
                    <h4 className="font-extrabold text-base text-white tracking-tight">
                      {activeEndorsement.endorserName}
                    </h4>
                    <p className="text-xs text-brand-300 font-medium">
                      {activeEndorsement.endorserTitle}
                    </p>
                  </div>
                  
                  <div className="text-xs text-slate-400 font-mono">
                    Endorsement for: <strong className="text-slate-200">{activeEndorsement.bookTitle}</strong> ({activeEndorsement.bookAuthor})
                  </div>
                </div>
              </div>

              {/* Right: Accompanying Book Thumbnail Card (Dynamically Linked) */}
              <div className="lg:col-span-4">
                <div className="p-4 rounded-2xl bg-slate-950/80 border border-slate-800 shadow-md flex items-center gap-4 transition-all">
                  <div className="w-20 aspect-[3/4] rounded-lg overflow-hidden bg-slate-900 border border-slate-700 shrink-0 p-1 flex items-center justify-center">
                    <img
                      src={activeEndorsement.coverImage}
                      alt={activeEndorsement.bookTitle}
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>
                  <div className="space-y-1 overflow-hidden">
                    <span className="text-[10px] font-mono text-brand-400 uppercase font-semibold block">
                      {activeEndorsement.formatBadge}
                    </span>
                    <h5 className="font-serif font-bold text-sm text-white truncate">
                      {activeEndorsement.bookTitle}
                    </h5>
                    <p className="text-xs text-slate-400">
                      By {activeEndorsement.bookAuthor}
                    </p>
                    <a
                      href={activeEndorsement.productUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[11px] text-brand-400 hover:text-brand-300 font-bold pt-1"
                    >
                      <span>View in Bookstore</span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              </div>

            </div>

          </div>

        </div>
      </section>

      {/* 4. CORPORATE PUBLISHING OPERATIONS (REPLACES DENSE TEXT WALL) */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        <div className="text-center space-y-2 max-w-2xl mx-auto">
          <span className="text-xs font-mono uppercase tracking-widest text-brand-600 font-bold">
            Corporate Architecture
          </span>
          <h2 className="text-2xl sm:text-3xl font-serif font-bold text-slate-900">
            Publishing Infrastructure &amp; Capabilities
          </h2>
          <p className="text-slate-600 text-xs sm:text-sm">
            Four specialized divisions powering distribution, translation rights, and academic publishing across India.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          
          {/* Pillar 1 */}
          <div className="p-6 rounded-2xl bg-white border border-slate-200/90 shadow-2xs hover:border-brand-300 hover:shadow-xs transition-all space-y-3">
            <div className="w-10 h-10 rounded-xl bg-brand-50 border border-brand-200 flex items-center justify-center text-brand-700">
              <BookMarked className="w-5 h-5" />
            </div>
            <h3 className="font-serif font-bold text-base text-slate-900">
              Pan-India Distribution
            </h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Nationwide retail and wholesale logistics supplying bookstores, churches, academic seminaries, and readers across India with over 430 titles.
            </p>
            <div className="pt-2">
              <Link to="/publications" className="text-xs font-bold text-brand-700 hover:text-brand-800 inline-flex items-center gap-1">
                <span>Browse Catalog</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>

          {/* Pillar 2 */}
          <div className="p-6 rounded-2xl bg-white border border-slate-200/90 shadow-2xs hover:border-brand-300 hover:shadow-xs transition-all space-y-3">
            <div className="w-10 h-10 rounded-xl bg-brand-50 border border-brand-200 flex items-center justify-center text-brand-700">
              <Languages className="w-5 h-5" />
            </div>
            <h3 className="font-serif font-bold text-base text-slate-900">
              Vernacular Translations
            </h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Licensed translation and publication of foundational Christian works into Hindi, Tamil, Telugu, Malayalam, Bengali, and other major Indian languages.
            </p>
            <div className="pt-2">
              <Link to="/services" className="text-xs font-bold text-brand-700 hover:text-brand-800 inline-flex items-center gap-1">
                <span>Translation Pipeline</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>

          {/* Pillar 3 */}
          <div className="p-6 rounded-2xl bg-white border border-slate-200/90 shadow-2xs hover:border-brand-300 hover:shadow-xs transition-all space-y-3">
            <div className="w-10 h-10 rounded-xl bg-brand-50 border border-brand-200 flex items-center justify-center text-brand-700">
              <Globe2 className="w-5 h-5" />
            </div>
            <h3 className="font-serif font-bold text-base text-slate-900">
              Global Imprint Rights
            </h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Strategic rights licensing agreements with leading international reformational publishers to produce accessible, high-quality Indian editions.
            </p>
            <div className="pt-2">
              <Link to="/contact" className="text-xs font-bold text-brand-700 hover:text-brand-800 inline-flex items-center gap-1">
                <span>Rights Enquiries</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>

          {/* Pillar 4 */}
          <div className="p-6 rounded-2xl bg-white border border-slate-200/90 shadow-2xs hover:border-brand-300 hover:shadow-xs transition-all space-y-3">
            <div className="w-10 h-10 rounded-xl bg-brand-50 border border-brand-200 flex items-center justify-center text-brand-700">
              <Shield className="w-5 h-5" />
            </div>
            <h3 className="font-serif font-bold text-base text-slate-900">
              Academic &amp; Editorial Press
            </h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Chicago Manual of Style editorial standards, doctrinal fidelity, classical interior typography, and durable archival-quality book production.
            </p>
            <div className="pt-2">
              <Link to="/our-process" className="text-xs font-bold text-brand-700 hover:text-brand-800 inline-flex items-center gap-1">
                <span>Publishing Process</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>

        </div>
      </section>

      {/* 5. CURATED PUBLICATIONS FROM STORE CATALOG */}
      <section className="bg-slate-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div className="space-y-1 max-w-xl">
              <span className="text-xs font-mono uppercase tracking-widest text-brand-400 font-bold">
                Selected Published Works
              </span>
              <h2 className="text-2xl sm:text-3xl font-serif font-bold text-slate-100">
                Titles of Theological &amp; Pastoral Depth
              </h2>
            </div>

            <Link
              to="/publications"
              className="inline-flex items-center gap-1.5 text-xs font-bold text-brand-400 hover:text-brand-300 shrink-0 uppercase tracking-wider"
            >
              <span>View All 432 Publications</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {publications.slice(0, 4).map((book: any) => (
              <div
                key={book.id}
                className="group flex flex-col justify-between bg-slate-950 rounded-2xl overflow-hidden border border-slate-800 hover:border-brand-500/50 shadow-lg transition-all"
              >
                <div className="aspect-[3/4] bg-slate-950 overflow-hidden relative flex items-center justify-center p-4 border-b border-slate-800/80">
                  <img
                    src={book.coverImage}
                    alt={book.title}
                    className="max-h-full max-w-full object-contain drop-shadow-md group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-2.5 left-2.5 bg-slate-900/90 text-brand-300 text-[9px] font-mono px-2 py-0.5 rounded uppercase font-bold border border-brand-500/30">
                    {book.format}
                  </div>
                </div>

                <div className="p-4 space-y-3 flex-1 flex flex-col justify-between">
                  <div className="space-y-1">
                    <span className="text-[10px] text-brand-400 font-mono font-medium block truncate">
                      {book.category}
                    </span>
                    <h3 className="font-serif text-sm font-bold text-white group-hover:text-brand-300 transition-colors line-clamp-1">
                      {book.title}
                    </h3>
                    <p className="text-xs text-slate-400 truncate">
                      By {book.author}
                    </p>
                  </div>

                  <div className="pt-3 border-t border-slate-800/80 flex items-center justify-between text-xs">
                    <Link
                      to={`/publications/${book.slug}`}
                      className="text-white hover:text-brand-400 font-medium text-xs"
                    >
                      Details &rarr;
                    </Link>
                    {book.bookstoreUrl && (
                      <a
                        href={book.bookstoreUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-brand-400 hover:text-brand-300 font-semibold text-xs"
                      >
                        <span>Bookstore</span>
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* 6. SUPPORTING CRITICAL ACCLAIM & ACADEMIC VOICES */}
      {testimonials.length > 1 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          <div className="text-center space-y-1 max-w-xl mx-auto">
            <span className="text-xs font-mono uppercase tracking-widest text-brand-600 font-bold">
              Scholarly Voices
            </span>
            <h2 className="text-xl sm:text-2xl font-serif font-bold text-slate-900">
              Institutional Commendation
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {testimonials.slice(1, 3).map((t: any) => (
              <div
                key={t.id}
                className="p-6 rounded-2xl bg-white border border-slate-200/90 shadow-2xs space-y-4 flex flex-col justify-between"
              >
                <p className="font-serif italic text-slate-700 text-sm sm:text-base leading-relaxed">
                  &ldquo;{t.quote}&rdquo;
                </p>
                <div className="flex items-center gap-3 pt-3 border-t border-slate-100">
                  <div className="w-10 h-10 rounded-full overflow-hidden bg-slate-200 border border-slate-200 shrink-0">
                    <img src={t.avatarUrl} alt={t.clientName} className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <h4 className="font-bold text-xs text-slate-900">{t.clientName}</h4>
                    <p className="text-[11px] text-slate-500">{t.authorTitle} &bull; <em>{t.bookTitle}</em></p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* 7. HIGH IMPACT CORPORATE & RIGHTS CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative overflow-hidden rounded-3xl bg-slate-950 text-white p-8 sm:p-12 border border-slate-800 text-center space-y-6">
          <div className="max-w-2xl mx-auto space-y-3">
            <span className="text-[11px] font-mono uppercase tracking-widest text-brand-400 font-bold">
              Institutional &amp; Publishing Inquiries
            </span>
            <h2 className="text-2xl sm:text-4xl font-serif font-bold text-slate-100">
              Partner With FOR THE TRUTH PRESS PRIVATE LIMITED
            </h2>
            <p className="text-slate-300 text-xs sm:text-sm font-normal max-w-lg mx-auto leading-relaxed">
              Whether you are an international rights holder seeking Indian distribution, an academic institution ordering in bulk, or an author submitting a scholarly manuscript.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-1">
            <Link
              to="/submit-enquiry"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-brand-500 hover:bg-brand-600 text-slate-950 font-bold text-xs uppercase tracking-wider shadow-lg shadow-brand-500/20 transition-all hover:scale-[1.01]"
            >
              <span>Submit Publishing Proposal (RFQ)</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/contact"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-slate-900 hover:bg-slate-850 text-white font-semibold text-xs border border-slate-700 transition-all"
            >
              <span>Corporate &amp; Rights Office</span>
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
};
